import React, { useState } from "react";
import {
  Stack,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  Divider,
  IconButton,
} from "@mui/material";
import { Storefront, Close } from "@mui/icons-material";

interface FavoriteStore {
  storeId?: string;
  storeName?: string | null;
  isDefault?: boolean;
  [key: string]: unknown;
}

interface Customer {
  favoriteStoresList?: FavoriteStore[];
}

interface RestaurantInfoProps {
  customer: Customer;
}

function formatStoreName(store: FavoriteStore): string {
  if (store.storeName) {
    return `${store.storeName} (${store.storeId ?? "N/A"})`;
  }
  return store.storeId ?? "N/A";
}

export const RestaurantInfo = ({ customer }: RestaurantInfoProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const stores: FavoriteStore[] = customer?.favoriteStoresList ?? [];

  const getSingleStoreDisplay = (): string => {
    if (stores.length === 0) {
      return "No Favorite Store";
    }
    return formatStoreName(stores[0]);
  };

  return (
    <>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" fontWeight={600}>Favorite Store:</Typography>
          {stores.length > 1 ? (
            <Button
              size="small"
              startIcon={<Storefront sx={{ fontSize: 16 }} />}
              onClick={() => setDialogOpen(true)}
              sx={{ textTransform: "none", fontSize: "0.8rem", p: 0 }}
            >
              {stores.length} stores
            </Button>
          ) : (
            <Typography variant="body2">
              {getSingleStoreDisplay()}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Favorite Stores ({stores.length})
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List sx={{ p: 0 }}>
            {stores.map((store, index) => (
              <React.Fragment key={store.storeId ?? index}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Storefront sx={{ fontSize: 18, color: "#041E42" }} />
                          <Typography variant="body1" fontWeight="bold">
                            Store {store.storeId ?? "N/A"}
                          </Typography>
                        </Box>
                        {store.isDefault && (
                          <Chip label="Default" size="small" color="primary" sx={{ height: 20, fontSize: "0.7rem" }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 3.5 }}>
                        {store.storeName ?? "No store name"}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < stores.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            variant="outlined"
            sx={{
              color: "#041E42",
              borderColor: "#041E42",
              "&:hover": {
                borderColor: "#0a3d7a",
                backgroundColor: "rgba(4, 30, 66, 0.04)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
